/**
 * @file FooterColumnsEditor.tsx
 * @description «Меню → Подвал» колонками (phase19.16, приёмка Р2а; SB113 —
 * колонки задаёт меню, до 3, пустая в лимите; SB114 — «О магазине» первая;
 * SB115 — пометка дубля ссылки платформы; uiux-footer-columns §4.6, §4.7,
 * §5): карточка «О магазине», карточки колонок (перетаскивание колонок и
 * пунктов между колонками — один `DndContext` с несколькими контейнерами),
 * добавление колонки сразу, без окна; `?column=<id>` — прокрутка к карточке
 * и подсветка. Раскладку считает общий `footerLayout` (как сервер и витрина).
 */

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { SiteTheme } from "@chaiberi/shared";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import type { ConfirmRequest } from "../../ui/use-confirm";
import { footerItemActions } from "./footer-menu-actions";
import { FooterAboutCard } from "./FooterAboutCard";
import { FooterColumnCard } from "./FooterColumnCard";
import { FooterColumnsToolbar } from "./FooterColumnsToolbar";
import { MenuItemRow } from "./MenuItemRow";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { useFooterMenuEditor } from "../../../hooks/site-view/useFooterMenuEditor";
import { useFooterSources } from "../../../hooks/site-view/useFooterSources";
import type { SiteMenusApi } from "../../../hooks/site-view/useSiteMenus";
import { aboutSummary, cabinetFooterLayout } from "../../../lib/site-view/footer-columns";
import { columnDndId, footerCollision, footerDrop } from "../../../lib/site-view/footer-dnd";
import type { SiteBrandDraft, SiteMenuItem } from "../../../lib/site-view/types";

interface Props {
  api: SiteMenusApi;
  tree: SiteMenuItem[];
  footer: SiteTheme["footer"];
  brand: SiteBrandDraft;
  narrow: boolean;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  onEdit: (item: SiteMenuItem) => void;
  onAddItem: (columnId: string) => void;
  /** «Переключить» подвал на «Колонками»; нет — кнопки нет. */
  onColumnsTemplate?: () => void;
}

const isColumn = (n: SiteMenuItem) => n.linkType === "GROUP" || n.linkType === "CATALOG_CATEGORIES";

export function FooterColumnsEditor({ api, tree, footer, brand, narrow, confirm, onEdit, onAddItem, onColumnsTemplate }: Props) {
  const link = useBuilderLink();
  const sources = useFooterSources();
  const editor = useFooterMenuEditor({ api, tree, confirm, onEdit });
  const [params] = useSearchParams();
  const anchor = params.get("column");
  // Подсветка карточки по `?column=` гаснет через 2,5 с: запоминаем, какую ссылку уже «погасили».
  const [dimmed, setDimmed] = useState<string | null>(null);
  const highlight = anchor !== null && anchor !== dimmed ? anchor : null;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const layout = useMemo(
    () => cabinetFooterLayout(tree, { footer, brand, returnsDocId: sources.returnsDocId, catalogCategoriesCount: sources.autoCategories?.length }),
    [tree, footer, brand, sources],
  );
  const duplicates = useMemo(() => new Set(layout.duplicates.flatMap((d) => (d.itemId ? [d.itemId] : []))), [layout]);
  const columns = tree.filter(isColumn);
  const loose = tree.filter((n) => !isColumn(n));

  // Ссылка из схемы вкладки «Подвал»: прокрутить к колонке и подсветить её.
  useEffect(() => {
    if (!anchor) return;
    document.getElementById(`footer-column-${anchor}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    const timer = window.setTimeout(() => setDimmed(anchor), 2500);
    return () => window.clearTimeout(timer);
  }, [anchor]);

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over) return;
    const next = footerDrop(tree, String(active.id), String(over.id));
    if (next) editor.handlers.reorder(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {footer.template === "row" ? (
        <NoticeStrip action={onColumnsTemplate ? <Button variant="outline" onClick={onColumnsTemplate}>Переключить</Button> : undefined}>
          Подвал сейчас «в строку» — колонки и заголовки видны только в шаблоне «Колонками».
        </NoticeStrip>
      ) : null}
      <p className="type-caption text-on-surface-variant">Колонки идут слева направо. Пункты можно перетаскивать между колонками.</p>
      <FooterAboutCard summary={aboutSummary(layout.about.parts)} href={link({ sel: { kind: "area", area: "footer" } })} />
      {layout.overLimit ? (
        <NoticeStrip tone="error">
          Колонок меню {layout.columns.length}, а помещается {layout.limit} — сайт не опубликуется. Объедините колонки или удалите лишнюю.
        </NoticeStrip>
      ) : null}
      {columns.length === 0 && loose.length === 0 ? (
        <NoticeStrip>В подвале пока нет колонок меню. Добавьте первую — например, «Покупателям» с «О нас» и «Вопросы».</NoticeStrip>
      ) : null}
      {/* Отступ снизу — высота липкой полосы «+ Колонка»: последняя карточка не уходит под неё. */}
      <div className={narrow ? "flex flex-col gap-3 pb-16" : "flex flex-col gap-3"}>
        <DndContext sensors={sensors} collisionDetection={footerCollision} onDragEnd={onDragEnd}>
          <SortableContext items={columns.map((c) => columnDndId(c.id))} strategy={verticalListSortingStrategy}>
            {columns.map((column, index) => (
              <FooterColumnCard
                key={column.id}
                column={column}
                index={index}
                info={layout.columns.find((c) => c.id === column.id)}
                tree={tree}
                handlers={editor.handlers}
                duplicates={duplicates}
                autoCategories={sources.autoCategories}
                highlighted={highlight === column.id}
                onAddItem={onAddItem}
                onRename={editor.rename}
                onLimit={editor.setLimit}
              />
            ))}
          </SortableContext>
          {loose.length > 0 ? (
            <div className="flex flex-col gap-2">
              <NoticeStrip tone="warning">Пункты вне колонки: {loose.length}. Перенесите их в колонку через «⋯» — в подвале пункт живёт в колонке.</NoticeStrip>
              <SortableContext items={loose.map((n) => n.id)} strategy={verticalListSortingStrategy}>
                {loose.map((item) => (
                  <MenuItemRow key={item.id} item={item} nested={false} actions={footerItemActions(tree, item, editor.handlers)} />
                ))}
              </SortableContext>
            </div>
          ) : null}
        </DndContext>
      </div>
      <FooterColumnsToolbar
        count={layout.columns.length}
        limit={layout.limit}
        hasEmpty={layout.emptyColumns > 0}
        canAddAuto={!columns.some((c) => c.linkType === "CATALOG_CATEGORIES")}
        busy={editor.busy}
        narrow={narrow}
        onAdd={(kind) => void editor.addColumn(kind)}
      />
    </div>
  );
}
